/**
 * 일별 이용률 — 수요의 주기가 여기서 보인다 (DESIGN §8.9).
 *
 * **관측되지 않은 날은 선을 잇지 않는다.** `missing_dates` 를 0 으로 채우거나
 * 양옆을 직선으로 이으면 "그날 아무도 안 썼다" 또는 "그날도 평소만큼 썼다"는
 * 관측하지 않은 주장이 된다 (절대원칙 2). `null` 로 두어 선을 끊고, 끊긴
 * 날짜를 각주에 적는다.
 *
 * **커버리지를 같은 축에 함께 그린다.** 이용률이 떨어진 날이 정말 덜 쓰인
 * 날인지, 그날 관측이 얇았던 것인지를 한 화면에서 갈라야 한다. 둘 다 0~1
 * 비율이라 축은 하나로 충분하다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { pct } from '@/lib/fmt'
import type { Envelope, TimeseriesData } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']

export function UtilizationTrend() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/timeseries', 'util', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<TimeseriesData>('/metrics/timeseries', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<TimeseriesData> | undefined
  const points = useMemo(() => env?.data.points ?? [], [env])
  const missing = env?.data.missing_dates ?? []

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')
    const na = cssVar('--c-na-ink', '#5f5f65')

    return {
      grid: { left: 46, right: 14, top: 28, bottom: 42 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params]
          const i = (arr[0] as { dataIndex: number }).dataIndex
          const p = points[i]
          if (!p) return ''
          const d = new Date(`${p.date}T00:00:00+09:00`)
          return `<b>${p.date}</b> (${WEEKDAY[d.getDay()]})<br/>이용률 ${pct(
            p.utilization,
          )}<br/>커버리지 ${pct(p.coverage)}`
        },
      },
      xAxis: {
        type: 'category',
        data: points.map((p) => p.date.slice(5)),
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, interval: Math.max(0, Math.floor(points.length / 8)) },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 1,
        name: '비율',
        nameTextStyle: { fontSize: 10, color: ink3, align: 'left' },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${(v * 100).toFixed(0)}%` },
      },
      series: [
        {
          name: '이용률',
          type: 'line',
          // null 은 선을 끊는다 — connectNulls 를 켜면 관측 없는 구간을 이어
          // 그려 관측하지 않은 값을 주장하게 된다
          connectNulls: false,
          data: points.map((p) => p.utilization),
          lineStyle: { width: 2, color: c1 },
          itemStyle: { color: c1, borderWidth: 2, borderColor: canvas },
          symbolSize: 8,
        },
        {
          name: '커버리지',
          type: 'line',
          connectNulls: false,
          data: points.map((p) => p.coverage),
          lineStyle: { width: 2, color: na, type: 'dotted' },
          itemStyle: { color: na },
          symbol: 'none',
        },
      ],
    }
  }, [points])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">일별 이용률</caption>
        <thead>
          <tr>
            <th scope="col">날짜</th>
            <th scope="col">요일</th>
            <th scope="col">이용률</th>
            <th scope="col">커버리지</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => {
            const d = new Date(`${p.date}T00:00:00+09:00`)
            return (
              <tr key={p.date}>
                <th scope="row">{p.date}</th>
                <td>{WEEKDAY[d.getDay()]}</td>
                <td className="num">{pct(p.utilization)}</td>
                <td className="num">{pct(p.coverage)}</td>
              </tr>
            )
          })}
          {missing.map((d) => (
            <tr key={d} className="is-na">
              <th scope="row">{d}</th>
              <td colSpan={3}>관측 없음 — 0 이 아니라 모름</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="일별 이용률"
      question="언제 더 쓰이는가 — 그리고 그 변화가 관측량으로 설명되지는 않는가"
      meta={env?.meta}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={points.length === 0}
      onRetry={() => void q.refetch()}
      legend={[
        { label: '이용률', color: cssVar('--c-cat-1', '#0066cc'), pattern: 'line' },
        { label: '관측 커버리지', color: cssVar('--c-na-ink', '#5f5f65'), pattern: 'line' },
      ]}
      encoding={[
        { role: '가로축', what: 'KST 일자' },
        { role: '세로축', what: '비율 (0~100%) — 두 계열이 같은 축을 쓴다' },
        { role: '실선', what: '이용률' },
        { role: '점선', what: '관측 커버리지' },
      ]}
      reading="이용률이 낮은 날이 정말 덜 쓰인 날인지, 그날 관측이 얇았던 것인지를 함께 봅니다."
      notes={[
        {
          label: '끊긴 선',
          text:
            '관측이 없는 날은 선을 잇지 않습니다. 0 으로 채우거나 양옆을 직선으로 이으면 관측하지 않은 값을 주장하게 됩니다.',
        },
        ...(missing.length > 0
          ? [{ label: '관측 없는 날', text: `${missing.length}일 — 표 보기 아래쪽에 회색으로 적었습니다.` }]
          : []),
      ]}
      table={table}
    >
      <EChart option={option} height={CHART_H} ariaLabel="일별 이용률 꺾은선그래프" />
    </ChartFrame>
  )
}
